import React, { useState } from "react";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { sendInvite } from "../../services/sendInviteService";
import { toast } from "react-toastify";
import { SendInviteResponse } from "../../proto/api_pb";
import { SendInviteModalProps } from "./types";
import Mixpanel from "../../utils/mixpanel";
import { Button, Dialog } from "../ui";

export const SendInviteModal: React.FC<SendInviteModalProps> = ({
  isSendInviteModalOpen,
  setIsSendInviteModalOpen,
  userId,
}) => {
  const [email, setEmail] = useState<string>("");
  const [listOfMails, setListOfMails] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { getValidAccessToken } = useGetValidAccessToken();

  const handleAddEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(email)) {
      setListOfMails([...listOfMails, email]);
      setEmail("");
    } else {
      toast.warning("Invalid email address");
    }
  };

  const handleDeleteEmail = (email: string) => {
    const newList = listOfMails.filter((el) => el !== email);
    setListOfMails(newList);
  };

  const handleSendInvite = async () => {
    setLoading(true);
    const accessToken = await getValidAccessToken();
    const response = await sendInvite(accessToken, listOfMails, userId);
    if (response instanceof SendInviteResponse) {
      setIsSendInviteModalOpen(false);
      setLoading(false);
      Mixpanel.track("invite_friends_success", { count: listOfMails.length });
      toast.success("Invited successfully");
    } else {
      setLoading(false);
      Mixpanel.track("invite_friends_failure");
      toast.error("Error while inviting");
    }
  };

  return (
    <Dialog.Root
      open={isSendInviteModalOpen}
      onOpenChange={(o) => {
        if (!o) setIsSendInviteModalOpen(false);
      }}
    >
      <Dialog.Content size="md" aria-describedby={undefined}>
        <Dialog.Header title="Invite Friends to OmMuse" />

        <p className="text-[13.5px] text-muted mb-4">
          Invite collaborators and team members to join your workspace.
        </p>

        {listOfMails.length > 0 && (
          <div className="flex flex-col gap-1.5 mb-4 max-h-[160px] overflow-y-auto">
            {listOfMails.map((el) => (
              <div
                key={el}
                className="flex items-center justify-between rounded-lg border border-border bg-surface-2 px-3 py-2 text-[13px] text-body"
              >
                <div className="flex items-center gap-2 truncate">
                  <EmailIcon sx={{ fontSize: 16, color: 'var(--cyan)' }} />
                  <span className="truncate">{el}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteEmail(el)}
                  className="appearance-none border-0 bg-transparent text-muted hover:text-danger cursor-pointer p-0"
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 mb-6">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEmail();
              }
            }}
            placeholder="Enter friend's email address..."
            className="flex-1 min-w-0 h-10 rounded-lg border border-border bg-surface-2 px-3 text-[13.5px] text-white placeholder:text-muted outline-none focus:border-cyan focus:shadow-[0_0_0_3px_var(--accent-soft)]"
          />
          <Button
            variant="ghost"
            onClick={handleAddEmail}
            disabled={email === ''}
            className="h-10 px-4 shrink-0"
          >
            Add
          </Button>
        </div>

        <Dialog.Footer>
          <Button
            variant="ghost"
            onClick={() => setIsSendInviteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={listOfMails.length === 0}
            loading={loading}
            onClick={handleSendInvite}
          >
            Send Invites {listOfMails.length > 0 ? `(${listOfMails.length})` : ''}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
